import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography, Button as AntdButton } from 'antd';
import { injectIntl } from 'react-intl';
import { useDispatch, connect } from 'react-redux';
import { UserAddOutlined } from '@ant-design/icons';

import RelationCard from 'components/Contacts/RelationCard/container';

import Button from 'components/Shared/Common/Button';
import ContactList from 'components/Shared/Contacts/ContactList';
import ContactSkeleton from 'components/Shared/Contacts/ContactSkeleton';
import ContactActions from 'components/Shared/User/Item/ContactActions';
import NavButtons from 'components/Shared/Common/NavButtons';

import { CONTACT_STATUS } from 'const/user';
import { requestUpdateContactStatus } from 'actions/user';
import { openDrawer } from 'actions/drawer';
import * as drawerTypes from 'types/drawer';

import { isWebRtcCompatible } from 'utils/webRTC';
import { getUrl } from 'utils/url';

const { Text } = Typography;

const ButtonStyle = styled(AntdButton)`
    display: flex;
    align-items: center;
    margin-top: 20px;
    margin-bottom: 20px;
`;

const StyledText = styled(Text)`
    &:hover {
        text-decoration: underline;
        cursor: pointer;
    }
`;

const StyledContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const RelationCardWithRequestPrompt = injectIntl(({ intl, ...props }) => {
    const [contactStatus, setContactStatus] = useState();
    const dispatch = useDispatch();
    const { id: _user } = props;

    const handleAccept = useCallback(() => {
        dispatch(requestUpdateContactStatus(_user, CONTACT_STATUS.ACCEPTED));
        setContactStatus(1);
    }, [dispatch, requestUpdateContactStatus, _user]);

    const handleDecline = useCallback(() => {
        dispatch(requestUpdateContactStatus(_user, CONTACT_STATUS.DECLINED));
        setContactStatus(2);
    }, [dispatch, requestUpdateContactStatus, _user]);

    return (
        <RelationCard
            {...props}
            renderActions={({ id }) => {
                if (!id) return null;

                if (contactStatus === CONTACT_STATUS.ACCEPTED) {
                    return [
                        <Text type="success">{intl.formatMessage({ id: 'global.loading' })}</Text>
                    ];
                }
                if (contactStatus === CONTACT_STATUS.DECLINED) {
                    return [
                        <Text type="danger">{intl.formatMessage({ id: 'global.loading' })}</Text>
                    ];
                }
                return [
                    <Button key="decline" color="secondary" danger onClick={handleDecline}>
                        {intl.formatMessage({ id: 'profile.relationButton.label.dismiss' })}
                    </Button>,
                    <Button key="accept" color="primary" onClick={handleAccept}>
                        {intl.formatMessage({ id: 'profile.relationButton.label.accept' })}
                    </Button>
                ];
            }}
        />
    );
});

export const getUserActions = onDelete => props => {
    const { id: _user, contactStatus } = props;
    const messageUrl = getUrl('feedByUser', { _user });
    const hasWebrtc = isWebRtcCompatible();
    const roomUrl = getUrl('roomByUser', { _user });

    if (contactStatus === CONTACT_STATUS.PENDING) {
        return [
            <ContactActions
                key="cancelRequest"
                _user={_user}
                contactStatus={contactStatus}
                isInitiator
                size="md"
            />
        ];
    }

    return [
        <Button
            style={{ width: 'auto' }}
            key="gotToFeed"
            isTransparent
            iconName="MessageCircle"
            size="md"
            href={messageUrl}
        />,
        <Button
            style={{ width: 'auto' }}
            key="PhoneCall"
            isTransparent
            iconName={hasWebrtc ? 'Phone' : 'PhoneOff'}
            isDisabled={!hasWebrtc}
            size="md"
            href={roomUrl}
            target={`roomByUser-${_user}`}
        />,
        <Button
            key="deleteContact"
            color="error"
            isTransparent
            size="md"
            onClick={() => onDelete(_user)}
            iconName="Trash2"
        />
    ].filter((_, i) => !!onDelete || i !== 2);
};

const RelationCardConnected = onDelete => props => (
    <RelationCard {...props} renderActions={getUserActions(onDelete)} />
);

const getNavButtons = (intl, contactStatus, setContactStatus) => [
    {
        type: 'link',
        children: intl.formatMessage({ id: 'contact.tabs.label.accepted' }),
        disabled: contactStatus === CONTACT_STATUS.ACCEPTED,
        onClick: () => setContactStatus(CONTACT_STATUS.ACCEPTED)
    },
    {
        type: 'link',
        children: intl.formatMessage({ id: 'contact.tabs.label.waiting' }),
        disabled: contactStatus === CONTACT_STATUS.PENDING,
        onClick: () => setContactStatus(CONTACT_STATUS.PENDING)
    }
];

const Contacts = injectIntl(({ intl, query, onDelete, isPublic, toggleSearchDrawer }) => {
    const [contactStatus, setContactStatus] = useState(CONTACT_STATUS.ACCEPTED);
    const [hasPending, setHasPending] = useState(true);

    const navButtons = useMemo(
        () => getNavButtons(intl, contactStatus, setContactStatus),
        [intl, contactStatus]
    );

    return (
        <>
            <ContactList
                className={hasPending ? 'highlight' : undefined}
                contactStatus={CONTACT_STATUS.PENDING}
                isInitiator={false}
                pageSize={4}
                ItemComponent={RelationCardWithRequestPrompt}
                hasStatus={false}
                autoHide
                label={
                    <h2>
                        {intl.formatMessage({ id: 'pushNotification.contactInvitation.title' })}
                    </h2>
                }
                onData={data => setHasPending(!!data?.length)}
                hasPending={hasPending}
                SkeletonComponent={ContactSkeleton}
            />
            <StyledContainer>
                {isPublic && <NavButtons buttons={navButtons} />}
                {isPublic && (
                    <ButtonStyle type="text" onClick={toggleSearchDrawer}>
                        <UserAddOutlined />
                        <StyledText>
                            {intl.formatMessage({ id: 'contacts.createConnection' })}
                        </StyledText>
                    </ButtonStyle>
                )}
            </StyledContainer>
            <ContactList
                query={query}
                contactStatus={contactStatus}
                isInitiator={contactStatus === CONTACT_STATUS.PENDING ? true : undefined}
                pageSize={48}
                ItemComponent={RelationCardConnected(onDelete)}
                SkeletonComponent={ContactSkeleton}
            />
        </>
    );
});

const mapDispatchToProps = dispatch => ({
    toggleSearchDrawer: () => {
        dispatch(openDrawer(drawerTypes.DRAWER_SEARCH));
    }
});

Contacts.propTypes = {
    query: PropTypes.string,
    toggleSearchDrawer: PropTypes.func.isRequired,
    isPublic: PropTypes.bool,
    onDelete: PropTypes.func
};

export default connect(null, mapDispatchToProps)(memo(Contacts));
